import React, { useState, useEffect, useCallback } from 'react';

interface BotActivityLogEntry {
  id: string;
  character_id: string;
  character_name: string;
  action_type: string;
  item_id: string;
  quantity: number;
  price_per_unit: number;
  total_value: number;
  order_id?: string;
  counterparty_id?: string;
  counterparty_name?: string;
  region: string;
  details?: Record<string, unknown>;
  created_at: string;
}

interface BotActivityFeedProps {
  apiBaseUrl: string;
  adminKey: string;
}

const ACTION_TYPE_COLORS: Record<string, string> = {
  'item_generation': '#22c55e',
  'buy_order': '#3b82f6',
  'sell_order': '#f59e0b',
  'order_filled': '#8b5cf6',
  'order_placed': '#06b6d4',
  'order_expired': '#718096',
  'fill_order': '#3b82f6',
  'place_buy': '#3b82f6',
  'place_sell': '#ef4444',
  'cancel_order': '#a855f7',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  'item_generation': 'Generated',
  'buy_order': 'Buy Order',
  'sell_order': 'Sell Order',
  'order_filled': 'Filled',
  'order_placed': 'Placed',
  'order_expired': 'Expired',
  'fill_order': 'Fill Order',
  'place_buy': 'Buy Order',
  'place_sell': 'Sell Order',
  'cancel_order': 'Cancelled',
};

export const BotActivityFeed: React.FC<BotActivityFeedProps> = ({ apiBaseUrl, adminKey }) => {
  const [activities, setActivities] = useState<BotActivityLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [filterActionType, setFilterActionType] = useState<string>('');
  const [filterBot, setFilterBot] = useState<string>('');

  const fetchActivities = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/bot-activity?limit=50`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(Array.isArray(data.activities) ? data.activities : []);
      }
    } catch (err) {
      console.error('Error fetching bot activities:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey]);

  useEffect(() => {
    fetchActivities();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchActivities(), 5000);
    return () => clearInterval(interval);
  }, [fetchActivities, autoRefresh]);

  const filteredActivities = activities.filter(a => {
    if (filterActionType && a.action_type !== filterActionType) return false;
    if (filterBot && a.character_id !== filterBot) return false;
    return true;
  });

  const uniqueBots = Array.from(new Set(activities.map(a => a.character_id)));
  const uniqueActionTypes = Array.from(new Set(activities.map(a => a.action_type)));

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString();
  };

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
      }}>
        <h3 style={{ color: '#ffa500', fontSize: 14, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>BOT ACTIVITY FEED</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Auto-refresh
          </label>
          <button
            onClick={() => fetchActivities()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: isLoading ? '#2d3748' : '#4a5568',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              borderRadius: 2,
            }}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        marginBottom: 12,
      }}>
        <div>
          <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
            Filter by Action
          </label>
          <select
            value={filterActionType}
            onChange={(e) => setFilterActionType(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 11,
              borderRadius: 2,
            }}
          >
            <option value="">All Actions</option>
            {uniqueActionTypes.map(type => (
              <option key={type} value={type}>
                {ACTION_TYPE_LABELS[type] || type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
            Filter by Bot
          </label>
          <select
            value={filterBot}
            onChange={(e) => setFilterBot(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 11,
              borderRadius: 2,
            }}
          >
            <option value="">All Bots</option>
            {uniqueBots.map(botId => {
              const botName = activities.find(a => a.character_id === botId)?.character_name || botId;
              return (
                <option key={botId} value={botId}>
                  {botName}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div style={{
        backgroundColor: '#0b1220',
        border: '1px solid #4a5568',
        borderRadius: 0,
        maxHeight: 400,
        overflowY: 'auto',
      }}>
        {filteredActivities.length === 0 ? (
          <div style={{
            padding: 16,
            color: '#718096',
            fontSize: 12,
            textAlign: 'center',
          }}>
            No activities yet
          </div>
        ) : (
          <div>
            {filteredActivities.map((activity, idx) => (
              <div
                key={activity.id || idx}
                style={{
                  padding: '10px 12px',
                  borderBottom: idx < filteredActivities.length - 1 ? '1px solid #2d3748' : 'none',
                  display: 'grid',
                  gridTemplateColumns: '80px 120px 80px 80px 1fr 80px',
                  gap: 8,
                  alignItems: 'center',
                  fontSize: 11,
                  color: '#e2e8f0',
                }}
              >
                <div style={{ color: ACTION_TYPE_COLORS[activity.action_type] || '#a0aec0' }}>
                  {ACTION_TYPE_LABELS[activity.action_type] || activity.action_type}
                </div>
                <div style={{ color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {activity.character_name}
                </div>
                <div style={{ color: '#fbbf24' }}>
                  {activity.item_id}
                </div>
                <div style={{ color: '#22c55e', fontWeight: 600 }}>
                  x{activity.quantity}
                </div>
                <div style={{ color: '#a0aec0', fontSize: 10 }}>
                  {activity.action_type === 'item_generation' && activity.details?.reason === 'fulfill_unfilled_order'
                    ? 'Fulfilling order'
                    : activity.region}
                </div>
                <div style={{ color: '#718096', fontSize: 10, textAlign: 'right' }}>
                  {formatTime(activity.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{
        marginTop: 12,
        padding: 8,
        backgroundColor: '#0b1220',
        borderRadius: 0,
        fontSize: 10,
        color: '#a0aec0',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 12,
      }}>
        <div>
          <span style={{ color: '#718096', textTransform: 'uppercase', fontSize: 9 }}>Total Activities:</span> {activities.length}
        </div>
        <div>
          <span style={{ color: '#718096', textTransform: 'uppercase', fontSize: 9 }}>Generated Items:</span>{' '}
          <span style={{ color: '#22c55e' }}>
            {activities.filter(a => a.action_type === 'item_generation').reduce((sum, a) => sum + a.quantity, 0)}
          </span>
        </div>
        <div>
          <span style={{ color: '#718096', textTransform: 'uppercase', fontSize: 9 }}>Orders Placed:</span>{' '}
          <span style={{ color: '#3b82f6' }}>
            {activities.filter(a => a.action_type === 'buy_order' || a.action_type === 'sell_order').length}
          </span>
        </div>
      </div>
    </div>
  );
};
